import { motion } from 'framer-motion';
import { Target, Eye, Heart, Award, Users, BookOpen, Shield, Sparkles } from 'lucide-react';

const IMAGES = {
  students: 'https://customer-assets.emergentagent.com/job_heart-school-kids/artifacts/opd0l82s_secred%20new%2001.jfif',
  awards: 'https://customer-assets.emergentagent.com/job_heart-school-kids/artifacts/gdsm9xb4_f55c8095-49f2-4138-8cae-1ab16a988922.jfif',
};

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6 }
};

const About = () => {
  const whyChooseUs = [
    {
      icon: Heart,
      title: 'Loving Environment',
      description: 'We create a warm, nurturing atmosphere where every child feels valued and loved.',
      color: 'bg-gradient-to-br from-pink-400 to-rose-500',
    },
    {
      icon: Users,
      title: 'Small Class Sizes',
      description: 'Our low student-to-teacher ratio ensures personalized attention for each child.',
      color: 'bg-gradient-to-br from-cyan-400 to-blue-500',
    },
    {
      icon: BookOpen,
      title: 'Holistic Curriculum',
      description: 'We focus on cognitive, social, emotional, and physical development.',
      color: 'bg-gradient-to-br from-orange-400 to-amber-500',
    },
    {
      icon: Shield,
      title: 'Safe & Hygienic',
      description: '24/7 CCTV monitoring and strict hygiene protocols for your peace of mind.',
      color: 'bg-gradient-to-br from-teal-400 to-green-500',
    },
    {
      icon: Sparkles,
      title: 'Creative Learning',
      description: 'Art, music, dance, and hands-on activities that spark creativity.',
      color: 'bg-gradient-to-br from-purple-400 to-indigo-500',
    },
    {
      icon: Award,
      title: 'Experienced Faculty',
      description: 'Our teachers are trained professionals passionate about early childhood education.',
      color: 'bg-gradient-to-br from-green-400 to-emerald-500',
    },
  ];

  return (
    <div className="min-h-screen pt-28 bg-gradient-to-br from-cyan-50 via-pink-50 to-yellow-50" data-testid="about-page">
      {/* Hero Section */}
      <section className="py-12 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeInUp} className="text-center mb-12">
            <span className="font-accent text-xl text-pink-500 inline-block -rotate-2 mb-2">
              About Us
            </span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold bg-gradient-to-r from-pink-500 via-purple-500 to-cyan-500 bg-clip-text text-transparent mb-6">
              Welcome to Sacred Heart School
            </h1>
            <p className="font-body text-lg md:text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
              A premier preschool dedicated to nurturing young minds with love, care, and excellence in education since our establishment in Kalyan Nagar, Karnataka.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="rounded-[2.5rem] overflow-hidden shadow-xl border-4 border-white"
          >
            <img
              src={IMAGES.students}
              alt="Sacred Heart School students and teachers"
              className="w-full h-[300px] md:h-[400px] lg:h-[500px] object-cover"
              data-testid="about-hero-image"
            />
          </motion.div>
        </div>
      </section>

      {/* School Introduction */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div {...fadeInUp}>
              <h2 className="font-heading text-3xl md:text-4xl font-semibold text-slate-800 mb-6">
                Our Story
              </h2>
              <div className="space-y-4 font-body text-lg text-slate-600 leading-relaxed">
                <p>
                  Sacred Heart School was founded with a simple yet profound mission: to provide young children with a joyful and enriching educational experience that lays the foundation for lifelong learning.
                </p>
                <p>
                  Located in the peaceful neighborhood of Kalyan Nagar, our school has become a beloved institution for families seeking quality preschool education. We believe that the early years are crucial for a child's development, and we are committed to making these years memorable and meaningful.
                </p>
                <p>
                  Our approach combines traditional values with modern teaching methods, creating an environment where children can explore, discover, and grow at their own pace while developing the skills they need for future academic success.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-gradient-to-br from-rose-50 to-amber-50 p-8 md:p-10 rounded-[2rem]"
            >
              <div className="space-y-8">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-rose-500 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <Target className="w-7 h-7 text-white" strokeWidth={2.5} />
                  </div>
                  <div>
                    <h3 className="font-heading text-xl font-semibold text-slate-800 mb-2">Our Mission</h3>
                    <p className="font-body text-slate-600">
                      To nurture each child's unique potential through a balanced curriculum that emphasizes academic excellence, character development, and creative expression.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-sky-400 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <Eye className="w-7 h-7 text-white" strokeWidth={2.5} />
                  </div>
                  <div>
                    <h3 className="font-heading text-xl font-semibold text-slate-800 mb-2">Our Vision</h3>
                    <p className="font-body text-slate-600">
                      To be a leading preschool that inspires a love for learning, cultivates curiosity, and prepares children to become confident, compassionate individuals.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-amber-400 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <Heart className="w-7 h-7 text-white" strokeWidth={2.5} />
                  </div>
                  <div>
                    <h3 className="font-heading text-xl font-semibold text-slate-800 mb-2">Our Values</h3>
                    <p className="font-body text-slate-600">
                      Love, respect, integrity, and excellence guide everything we do. We treat every child as a unique gift deserving of our best efforts.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Principal's Message */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-white to-rose-50/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeInUp} className="text-center mb-12">
            <span className="font-accent text-xl text-rose-500 inline-block -rotate-2 mb-2">
              Leadership
            </span>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-semibold text-slate-800">
              Message from Our Leaders
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
            {/* Principal's Message */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="bg-white p-8 md:p-10 rounded-[2rem] shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-100"
              data-testid="principal-message"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-20 h-20 bg-gradient-to-br from-rose-400 to-rose-600 rounded-full flex items-center justify-center text-white font-heading text-2xl font-bold">
                  P
                </div>
                <div>
                  <h3 className="font-heading text-xl font-semibold text-slate-800">Principal's Message</h3>
                  <p className="font-body text-slate-500">School Principal</p>
                </div>
              </div>
              <div className="font-body text-slate-600 leading-relaxed space-y-4">
                <p>
                  "Dear Parents and Guardians,
                </p>
                <p>
                  Welcome to Sacred Heart School! It is my privilege to lead an institution that places children at the center of everything we do. Our dedicated team works tirelessly to create an environment where your little ones can thrive academically, socially, and emotionally.
                </p>
                <p>
                  We believe that early childhood education is the foundation upon which all future learning is built. That's why we focus on creating joyful, meaningful learning experiences that ignite curiosity and foster a lifelong love of learning.
                </p>
                <p>
                  I invite you to visit our campus and see firsthand the magic that happens when passionate educators and eager young minds come together. Together, let's give your child the best start in life."
                </p>
              </div>
              <p className="font-body text-sm text-slate-400 mt-6 italic">
                *Photo will be added soon
              </p>
            </motion.div>

            {/* Administrative Officer's Message */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.15 }}
              className="bg-white p-8 md:p-10 rounded-[2rem] shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-100"
              data-testid="ao-message"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-20 h-20 bg-gradient-to-br from-sky-400 to-sky-600 rounded-full flex items-center justify-center text-white font-heading text-2xl font-bold">
                  A
                </div>
                <div>
                  <h3 className="font-heading text-xl font-semibold text-slate-800">Administrative Officer's Message</h3>
                  <p className="font-body text-slate-500">Administrative Head</p>
                </div>
              </div>
              <div className="font-body text-slate-600 leading-relaxed space-y-4">
                <p>
                  "Dear Parents,
                </p>
                <p>
                  At Sacred Heart School, we understand that choosing a preschool for your child is one of the most important decisions you'll make. We are committed to making this journey smooth and transparent for every family.
                </p>
                <p>
                  Our administrative team ensures that all operations run seamlessly, from admissions to daily activities. We maintain open communication with parents through regular updates, meetings, and our WhatsApp helpline.
                </p>
                <p>
                  We take pride in our well-maintained facilities, efficient processes, and responsive support system. Should you have any questions or concerns, our doors are always open. We look forward to welcoming your family to our school community."
                </p>
              </div>
              <p className="font-body text-sm text-slate-400 mt-6 italic">
                *Photo will be added soon
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <span className="font-accent text-xl text-rose-500 inline-block -rotate-2 mb-2">
              Why Choose Us
            </span>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-semibold text-slate-800 mb-4">
              The Sacred Heart Difference
            </h2>
            <p className="font-body text-lg text-slate-600 max-w-2xl mx-auto">
              We go beyond academics to ensure your child receives the best possible start in life
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {whyChooseUs.map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-slate-50 p-8 rounded-[2rem] transition-all duration-300 hover:-translate-y-2 hover:shadow-xl hover:bg-white group"
                data-testid={`why-choose-${item.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className={`w-14 h-14 ${item.color} rounded-2xl flex items-center justify-center mb-6 transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3`}>
                  <item.icon className="w-7 h-7 text-white" strokeWidth={2.5} />
                </div>
                <h3 className="font-heading text-xl font-semibold text-slate-800 mb-3">
                  {item.title}
                </h3>
                <p className="font-body text-slate-600 leading-relaxed">
                  {item.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Awards Image */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-white to-amber-50/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeInUp} className="text-center mb-12">
            <span className="font-accent text-xl text-rose-500 inline-block -rotate-2 mb-2">
              Celebrating Excellence
            </span>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-semibold text-slate-800">
              Our Achievements
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="rounded-[2.5rem] overflow-hidden shadow-xl"
          >
            <img
              src={IMAGES.awards}
              alt="Sacred Heart School award ceremony"
              className="w-full h-[300px] md:h-[400px] lg:h-[500px] object-cover"
              data-testid="awards-image"
            />
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About;
